import { Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EMPTY, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { ProfileService } from 'src/app/profile/profile.service';
import { SignupService } from 'src/app/signup/signup.service';
import { AgentContactState } from 'src/app/workspace/state/agent-contact.state';
import { PropertyState } from 'src/app/workspace/state/property.state';
import { TermsPolicyDirective } from 'src/app/_shared/directive/terms-policy.directive';
import { UserState } from 'src/app/_shared/state/user.state';
import constants from 'src/constant.json';

interface DialogContent {
  content: string;
  proceedAction?: any;
  proceedCancelAction?: any;
  offerRequestData?: any;
}
interface CompanyPolicy {
  content: string;
  title: string;
  type: string;
}

@Component({
  selector: 'app-agent-contact-offer',
  templateUrl: './agent-contact-offer.component.html',
  styleUrls: ['./agent-contact-offer.component.scss'],
})
export class AgentContactOfferComponent implements OnInit, OnDestroy {
  @ViewChild('ctaPolicy') ctaPolicy: ElementRef;
  @ViewChildren(TermsPolicyDirective) termsAndPolicyDir;
  onDestroyNotifier$ = new Subject();
  enableSubmit = true;
  type: string;
  content: string;
  policy: Partial<CompanyPolicy> = {};
  checkBox: boolean;
  offerRequestForm: FormGroup;
  initial: boolean = true;
  termsAndConditions: Partial<CompanyPolicy> = {};
  makeOfferInProgress: boolean;
  companyPolicy: [];
  policyAgreement: Partial<CompanyPolicy> = {};
  userProfile: any;
  showPassword: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<AgentContactOfferComponent>,
    private renderer: Renderer2,
    @Inject(MAT_DIALOG_DATA) private data: DialogContent,
    private profileService: ProfileService,
    private formBuilder: FormBuilder,
    private agentContactState: AgentContactState,
    private signupService: SignupService,
    private appState: AppState,
    private propertyState: PropertyState,
    private snackBar: MatSnackBar,
    private userState: UserState
  ) {}

  ngAfterViewInit() {
    if (this.policyAgreement.content != null) {
      this.renderer.setProperty(this.ctaPolicy.nativeElement, 'innerHTML', this.policyAgreement.content);
      this.renderer.listen(this.ctaPolicy.nativeElement.querySelector('#terms'), 'click', (event) => {
        this.termsAndPolicyDir.first.triggerPolicyView('terms');
      });
      this.renderer.listen(this.ctaPolicy.nativeElement.querySelector('#policy'), 'click', (event) => {
        this.termsAndPolicyDir.first.triggerPolicyView('policy');
      });
    }
  }

  ngOnInit(): void {
    this.userState.userDetail$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      if (res) this.userProfile = res;
      this.userProfile = Object.assign({}, this.userProfile?.user_profile);
    });
    this.initial = true;
    this.offerRequestForm = this.formBuilder.group({
      firstName: [
        { value: this.userProfile?.firstName || '', disabled: false },
        [Validators.required, Validators.pattern('[a-zA-Z ]*')],
      ],
      lastName: [
        { value: this.userProfile?.lastName || '', disabled: false },
        [Validators.required, Validators.pattern('[a-zA-Z ]*')],
      ],
      email: [
        { value: this.userProfile?.email || '', disabled: false },
        [Validators.required, Validators.pattern(constants.regex.validEmail)],
      ],
      phone: [
        { value: this.userProfile?.contact || '', disabled: false },
        [
          Validators.required,
          Validators.pattern('^(\\+\\d{1,2}\\s?)?1?\\-?\\.?\\s?\\(?\\d{3}\\)?[\\s.-]?\\d{3}[\\s.-]?\\d{4}$'),
        ],
      ],
      offer: ['', [Validators.required, Validators.pattern('[0-9]*')]],
      planToBuy: ['', [Validators.required]],
      isScheduledPropertyVisit: ['', [Validators.required]],
      notes: ['', []],
    });
    if (this.appState.authTokenValue?.idToken) {
      this.offerRequestForm.controls['email'].disable();
    }
    this.agentContactState.contactAgentRequestActionStatus$
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((res) => {
        this.makeOfferInProgress = res;
      });
    this.appState.companyInfo$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (res) {
        this.companyPolicy = res['companyPolicy'];
        this.companyPolicy.forEach((data: any) => {
          if (data['type'] === 'ctaPolicy') {
            this.policyAgreement.content = data.content;
            this.checkBox = true;
          } else {
            this.checkBox = false;
          }
        });
      }
    });
  }

  ngOnDestroy() {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }

  requestOffer() {
    this.initial = false;
    if (this.offerRequestForm.invalid) return;
    this.data.offerRequestData = this.offerRequestForm.value;
    if (this.appState.authTokenValue?.idToken) {
      (this.userProfile.email = this.offerRequestForm.value.email),
        (this.userProfile.firstName = this.offerRequestForm.value.firstName),
        (this.userProfile.lastName = this.offerRequestForm.value.lastName),
        (this.userProfile.contact = this.offerRequestForm.value.phone);
      if (this.offerRequestForm.invalid) return;
      this.profileService
        .updateUserBasicInfo(this.userProfile)
        .pipe(takeUntil(this.onDestroyNotifier$))
        .subscribe((res) => {
          if (res.message) {
            this.data.proceedAction(this.data.offerRequestData, () => {});
          }
        });
    } else {
      let signUpParams = {
        email: this.offerRequestForm.value.email.toLowerCase(),
        firstName: this.offerRequestForm.value.firstName,
        lastName: this.offerRequestForm.value.lastName,
        phone: this.offerRequestForm.value.phone,
        role: 'employee',
        userId: this.agentContactState.propertyClientDataValue['agent'].shortId,
        domain: this.appState.appBaseUrlValue,
        interestedAreas: [
          {
            locality: this.propertyState.activePropertyDetailsValue['basic'].City,
            state: this.propertyState.activePropertyDetailsValue['basic'].StateOrProvince,
          },
        ],
        createdBy: 'system',
        source: constants.SIGN_UP.source.makeAnOfferCta,
      };
      this.agentContactState.contactAgentRequestActionStatusValue = true;
      this.signupService
        .createUser(signUpParams)
        .pipe(
          takeUntil(this.onDestroyNotifier$),
          map(() => {
            this.data.proceedAction(this.data.offerRequestData, () => {});
          }),
          catchError((error) => {
            this.agentContactState.contactAgentRequestActionStatusValue = false;

            const errorMsg = error?.error?.message || 'Failed to send an offer request';

            this.openSnackBar(errorMsg, 'snackbar-error');

            this.close();
            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  close() {
    this.dialogRef.close();
  }

  get offerRequestFormControls() {
    return this.offerRequestForm.controls;
  }

  openSnackBar(message, style) {
    if (message) {
      this.snackBar.open(message, '', {
        duration: 3000,
        panelClass: [style ? style : ''],
        verticalPosition: 'top',
      });
    }
  }

  // phoneValidator = (control) => {
  //   if (control.value != '') {
  //     const formatedValue = this.toNumberPipe.transform(control.value);
  //     if (formatedValue.toString().length == 10 || formatedValue.toString().length == 11) {
  //       console.log(formatedValue.toString().length);
  //       return null;
  //     } else return { phoneNumberInvalid: true };
  //   }
  // };
}
